import { useMemo } from 'react'

import { breakpointEntries } from 'components/utils/breakpoint'
import { ChertoExperiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import { isPaidSearch } from 'marketing/lib/lib'
import { isCroMobileCompactGeneralSearchPath } from './useCroMobileCompactSearchTileExperiment'

interface CroPaidSearchDesktopQuickSelectEligibility {
  /** Standard accommodation search pages opt in. Other `OfferCountAndSort` callers stay out. */
  readonly enabled: boolean
  /**
   * The results heading is actually showing this control.
   * Zero-result searches that unmount or hide the heading must pass false so they are not exposed.
   */
  readonly isControlVisible: boolean
  readonly headlessMode?: boolean
  readonly isMobileOrTablet: boolean
  readonly pathname: string
  readonly utmBp?: string | number | null
  /** Landmark results use a different sort list, including distance. */
  readonly isLandmarkSearch: boolean
}

export interface CroPaidSearchDesktopQuickSelectExperimentValue {
  readonly showQuickSelect: boolean
}

interface CroPaidSearchDesktopQuickSelectOptions {
  readonly enabled: boolean
  /**
   * SSR-stable display gate: the page opted in and the desktop heading is shown.
   * Viewport size and result loading do not belong here.
   */
  readonly isDisplayed: boolean
  /**
   * Exposure gate. Includes the display gate plus a settled result state
   * and a known viewport that meets the same breakpoint as the shortcut row.
   */
  readonly isReadyToExpose: boolean
  readonly isLandmarkSearch: boolean
}

/** Same `min` used by the desktop shortcut row and its exposure gate. */
export function getPaidSearchDesktopQuickSelectMinBreakpoint(
  showSearchVerticalTabs: boolean,
): 'tablet' | 'desktop' {
  return showSearchVerticalTabs ? 'desktop' : 'tablet'
}

/**
 * `undefined` means the viewport is not known yet, so activation stays off.
 */
export function isPaidSearchDesktopQuickSelectViewportVisible(
  viewport: App.ScreenSize | undefined,
  showSearchVerticalTabs: boolean,
): boolean {
  if (!viewport) return false

  const minBreakpoint = getPaidSearchDesktopQuickSelectMinBreakpoint(showSearchVerticalTabs)
  const minIndex = breakpointEntries.findIndex(([breakpoint]) => breakpoint === minBreakpoint)
  const viewportIndex = breakpointEntries.findIndex(([breakpoint]) => breakpoint === viewport)

  return viewportIndex >= minIndex
}

export function isPaidSearchDesktopQuickSelectAudience({
  enabled,
  isControlVisible,
  headlessMode = false,
  isMobileOrTablet,
  pathname,
  utmBp,
  isLandmarkSearch,
}: CroPaidSearchDesktopQuickSelectEligibility): boolean {
  return enabled &&
    isControlVisible &&
    !headlessMode &&
    !isMobileOrTablet &&
    !isLandmarkSearch &&
    isPaidSearch(utmBp) &&
    isCroMobileCompactGeneralSearchPath(pathname)
}

/**
 * Desktop paid-search sort shortcuts (`cro-paid-search-desktop-quick-select`).
 * `showQuickSelect` uses the SSR-stable audience and bucket.
 * Viewport readiness, the actual breakpoint, and result loading only affect exposure.
 * Control and variant share that exposure gate.
 */
function useCroPaidSearchDesktopQuickSelectExperiment(
  options: CroPaidSearchDesktopQuickSelectOptions,
): CroPaidSearchDesktopQuickSelectExperimentValue {
  const { enabled, isDisplayed, isReadyToExpose, isLandmarkSearch } = options
  const pathname = useAppSelector(state => state.router.location.pathname)
  const utmBp = useAppSelector(state => state.utm.bp)
  const headlessMode = useAppSelector(state => state.system.headlessMode)
  const isMobileOrTablet = useIsMobileOrTablet()

  const canShow = useMemo(
    () => isPaidSearchDesktopQuickSelectAudience({
      enabled,
      isControlVisible: isDisplayed,
      headlessMode,
      isMobileOrTablet,
      pathname,
      utmBp,
      isLandmarkSearch,
    }),
    [enabled, headlessMode, isDisplayed, isLandmarkSearch, isMobileOrTablet, pathname, utmBp],
  )
  const canActivate = useMemo(
    () => isPaidSearchDesktopQuickSelectAudience({
      enabled,
      isControlVisible: isReadyToExpose,
      headlessMode,
      isMobileOrTablet,
      pathname,
      utmBp,
      isLandmarkSearch,
    }),
    [enabled, headlessMode, isLandmarkSearch, isMobileOrTablet, isReadyToExpose, pathname, utmBp],
  )

  const { isAnyVariant } = useExperiment(ChertoExperiments.croPaidSearchDesktopQuickSelect, { canActivate })

  return useMemo(
    () => ({ showQuickSelect: canShow && isAnyVariant }),
    [canShow, isAnyVariant],
  )
}

export default useCroPaidSearchDesktopQuickSelectExperiment
